/**
 * Utilidades de fecha para evitar desfases por timezone al enviar al backend.
 *
 * Problema: toISOString() convierte a UTC. En zonas con UTC positivo (ej. Europa),
 * una fecha local "4 feb 00:00" se convierte a "2025-02-03T23:00:00.000Z", por lo que
 * el día enviado sería el 3 en lugar del 4 (o el backend puede interpretar un día de más).
 *
 * Solución: Usar siempre componentes de fecha en hora local (getFullYear, getMonth, getDate)
 * y enviar al backend como YYYY-MM-DD sin hora.
 */

export interface DateRange {
    from: Date;
    to: Date;
}

/**
 * Rango de fechas por defecto para filtros de búsqueda.
 * Desde: hoy - 14 días (2 semanas atrás)
 * Hasta: hoy + 21 días (3 semanas adelante)
 *
 * Centralizado para que todos los módulos usen el mismo rango
 * y se modifique en un solo lugar.
 */
export function getDefaultDateRange(): DateRange {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 14);
    const to = new Date(today);
    to.setDate(today.getDate() + 21);
    return { from, to };
}

/**
 * Formatea una fecha para envío al backend como YYYY-MM-DD en hora local.
 * Evita el desfase de un día que produce toISOString() en ciertos timezones.
 *
 * @param date - Fecha (típicamente la seleccionada en un datepicker)
 * @returns String YYYY-MM-DD en hora local del usuario
 */
export function formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parsea un string YYYY-MM-DD a Date en hora local (medianoche local).
 * Útil al restaurar fechas desde query params para que el datepicker muestre el día correcto
 * en cualquier timezone (evita que "2025-02-05" se parsee como 00:00 UTC y se vea como 4 feb en Chile).
 *
 * @param dateStr - String en formato YYYY-MM-DD
 * @returns Date en medianoche hora local, o Invalid Date si el string no es válido
 */
export function parseDateFromApi(dateStr: string): Date {
    if (!dateStr || typeof dateStr !== 'string') {
        return new Date(NaN);
    }
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) {
        return new Date(dateStr);
    }
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return new Date(NaN);
    }
    return new Date(year, month, day);
}
